import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SaleAdditionalItem } from '../../api/SalesAPI';
import { additionalItemLineTotal } from '../../utils/saleLineItems';
import Tooltip from '../Tooltip/Tooltip';

type Props = {
  items: SaleAdditionalItem[];
  currency: string;
  className?: string;
};

function formatItemLabel(item: SaleAdditionalItem): string {
  const name = item.itemName;
  const variant = item.productVariant ? ` (${item.productVariant})` : '';
  const quantity = item.quantity ?? 1;
  const quantitySuffix = quantity > 1 ? ` (×${quantity})` : '';
  return `${name}${variant}${quantitySuffix}`;
}

function SaleAdditionalItemsBadge({ items, currency, className = '' }: Props) {
  const { t } = useTranslation();

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [items]
  );

  if (sortedItems.length === 0) {
    return null;
  }

  const tooltipContent = (
    <div className="space-y-1">
      {sortedItems.map((item, index) => (
        <div key={item.id ?? index}>
          {formatItemLabel(item)}
          {' — '}
          {additionalItemLineTotal(item).toLocaleString()} {currency}
        </div>
      ))}
    </div>
  );

  return (
    <Tooltip content={tooltipContent} position="bottom" contentClassName="max-w-xs">
      <span className={`text-xs text-gray-500 cursor-default ${className}`.trim()}>
        {t('sales.list.additional_items', '+{{count}} items', { count: sortedItems.length })}
      </span>
    </Tooltip>
  );
}

export default SaleAdditionalItemsBadge;
